import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { HUB } from "../constants/testIds";
import {
  Flame, Brain, MessageSquareText, Calendar, ArrowUpRight, GraduationCap,
  Sparkles, UploadCloud, Layers, Timer, Trophy, Lock, Loader2, BarChart3
} from "lucide-react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

// Número que sobe animado ao carregar (respeita prefers-reduced-motion)
function CountUp({ value, suffix = "" }) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(reduced ? value : 0);
  useEffect(() => {
    if (reduced) { setDisplay(value); return; }
    const target = Number(value) || 0;
    if (target === 0) { setDisplay(0); return; }
    const dur = 900;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced]);
  return <>{display}{suffix}</>;
}

// Countdown vivo até a prova, com anel de progresso do tempo restante
function ExamCountdown({ exam, daysToExam }) {
  const reduced = useReducedMotion();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const examDate = new Date(exam.date + "T07:00:00");
  const created = exam.created_at ? new Date(exam.created_at) : null;
  const msLeft = Math.max(0, examDate - now);
  const dias = Math.floor(msLeft / 86400000);
  const horas = Math.floor((msLeft % 86400000) / 3600000);
  const mins = Math.floor((msLeft % 3600000) / 60000);
  const segs = Math.floor((msLeft % 60000) / 1000);

  // Fração do caminho já percorrido desde que o Modo Prova foi ativado
  let frac = 0;
  if (created && examDate > created) {
    frac = Math.min(1, Math.max(0, (now - created.getTime()) / (examDate - created.getTime())));
  }
  const R = 26;
  const C = 2 * Math.PI * R;

  return (
    <motion.div
      variants={item}
      data-testid={HUB.examCard}
      className="rounded-2xl border border-violet-500/40 bg-violet-500/10 p-5 exam-glow card-depth"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-violet-300 mb-3">
        <Sparkles className="h-3.5 w-3.5" /> Modo Prova ativo
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {/* Anel de progresso */}
        <div className="relative h-16 w-16 shrink-0">
          <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
            <circle cx="32" cy="32" r={R} fill="none" stroke="hsl(263 50% 30% / 0.5)" strokeWidth="5" />
            <motion.circle
              cx="32" cy="32" r={R} fill="none"
              stroke="hsl(263 80% 68%)" strokeWidth="5" strokeLinecap="round"
              strokeDasharray={C}
              initial={{ strokeDashoffset: C }}
              animate={{ strokeDashoffset: C * (1 - frac) }}
              transition={reduced ? { duration: 0 } : { duration: 1.2, ease: "easeOut" }}
            />
          </svg>
          <Timer className="absolute inset-0 m-auto h-5 w-5 text-violet-300" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <h2 className="text-lg font-semibold leading-tight">{exam.title}</h2>
          <p className="text-xs text-muted-foreground">{exam.subject}</p>
        </div>
        <div className="flex items-end gap-2 font-mono" data-testid="exam-countdown">
          <TimeCell v={dias} label="dias" big />
          <TimeCell v={horas} label="h" />
          <TimeCell v={mins} label="min" />
          <TimeCell v={segs} label="s" dim />
        </div>
      </div>
    </motion.div>
  );
}

function TimeCell({ v, label, big = false, dim = false }) {
  return (
    <div className="text-center">
      <div className={`${big ? "text-3xl" : "text-xl"} font-bold tabular-nums ${dim ? "text-muted-foreground" : "text-violet-200"}`}>
        {String(v).padStart(2, "0")}
      </div>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

// Faixa rotativa de prioridades reais (pausa no hover)
function Ticker({ items }) {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  useEffect(() => {
    if (reduced || items.length <= 1) return;
    const t = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % items.length);
    }, 4000);
    return () => clearInterval(t);
  }, [items.length, reduced]);
  if (items.length === 0) return null;
  const current = items[idx % items.length];
  return (
    <motion.div
      variants={item}
      data-testid="hub-ticker"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      className="rounded-2xl border border-border bg-card card-depth px-4 py-3 flex items-center gap-3 overflow-hidden"
    >
      <span className="shrink-0 rounded-lg bg-violet-500/15 p-1.5">
        <current.icon className="h-4 w-4 text-violet-400" />
      </span>
      <div className="flex-1 min-w-0 h-5 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 text-sm truncate"
          >
            {current.to ? (
              <Link to={current.to} className="hover:text-violet-300 transition-colors">{current.text}</Link>
            ) : current.text}
          </motion.div>
        </AnimatePresence>
      </div>
      {items.length > 1 && (
        <div className="flex gap-1 shrink-0">
          {items.map((_, i) => (
            <span key={i} className={`h-1.5 w-1.5 rounded-full transition ${i === idx ? "bg-violet-400" : "bg-muted-foreground/30"}`} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// Card compacto de progresso: streak · nível · barra de XP até o próximo nível.
// Discreto de propósito — engajamento sem virar joguinho.
function ProgressoCard({ progresso }) {
  if (!progresso) return null;
  const { streak_dias, nivel, xp, xp_no_nivel, xp_por_nivel } = progresso;
  if (!xp && !streak_dias) return null; // aluno novo: sem card vazio
  const pct = Math.min(100, Math.round((xp_no_nivel / (xp_por_nivel || 100)) * 100));
  return (
    <motion.div
      variants={item}
      data-testid="hub-progresso"
      className="rounded-2xl border border-border bg-card card-depth px-4 py-3 flex items-center gap-4 flex-wrap"
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium">
        <Flame className="h-4 w-4 text-orange-400" /> {streak_dias} dia{streak_dias === 1 ? "" : "s"} seguidos
      </span>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium">
        <Trophy className="h-4 w-4 text-amber-400" /> Nível {nivel}
      </span>
      <div className="flex-1 min-w-[140px] flex items-center gap-2">
        <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
          <div className="h-full rounded-full bg-violet-500 transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
          {xp_no_nivel}/{xp_por_nivel} XP
        </span>
      </div>
    </motion.div>
  );
}

// Relatório semanal — recurso Pro. Pros outros planos: cadeado com CTA de upgrade.
function RelatorioSemanal({ liberado }) {
  const [aberto, setAberto] = useState(false);
  const [rel, setRel] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const abrir = async () => {
    setAberto((a) => !a);
    if (rel || carregando) return;
    setCarregando(true);
    try {
      const { data } = await api.get("/me/relatorio-semanal", { silent: true });
      setRel(data);
    } catch {}
    setCarregando(false);
  };

  if (!liberado) {
    return (
      <motion.div variants={item} data-testid="hub-relatorio-locked" className="rounded-2xl border border-border bg-card card-depth p-4 flex items-center gap-3">
        <div className="rounded-xl bg-violet-500/10 p-2.5"><BarChart3 className="h-5 w-5 text-violet-400" strokeWidth={1.75} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">Relatório semanal <Lock className="h-3.5 w-3.5 text-violet-400" /></h3>
          <p className="text-xs text-muted-foreground">Sua semana em números + onde focar. Recurso do plano Pro.</p>
        </div>
        <Link to="/planos" data-testid="hub-relatorio-upgrade" className="shrink-0 rounded-full bg-violet-600 hover:bg-violet-500 text-white px-4 py-1.5 text-xs font-medium transition">
          Desbloquear
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div variants={item} data-testid="hub-relatorio" className="rounded-2xl border border-border bg-card card-depth p-4 space-y-3">
      <button onClick={abrir} className="w-full flex items-center gap-3 text-left">
        <div className="rounded-xl bg-violet-500/10 p-2.5"><BarChart3 className="h-5 w-5 text-violet-400" strokeWidth={1.75} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">Relatório semanal</h3>
          <p className="text-xs text-muted-foreground">Seus últimos 7 dias: o que estudou, acertos e onde focar.</p>
        </div>
        <ArrowUpRight className={`h-4 w-4 text-muted-foreground transition-transform ${aberto ? "rotate-90" : ""}`} />
      </button>
      {aberto && (
        <div className="pt-1 space-y-3">
          {carregando && (
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" /> Montando seu relatório...</p>
          )}
          {!carregando && rel && (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full border border-border bg-secondary px-3 py-1">{rel.mensagens} mensagens</span>
                <span className="rounded-full border border-border bg-secondary px-3 py-1">{rel.quizzes} quiz{rel.quizzes === 1 ? "" : "zes"}</span>
                {(rel.materias_mais_estudadas || []).slice(0, 3).map((m) => (
                  <span key={m.materia} className="rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 px-3 py-1">{m.materia}</span>
                ))}
              </div>
              {(rel.acerto_por_materia || []).filter((a) => a.pct != null).length > 0 && (
                <div className="space-y-1.5">
                  {rel.acerto_por_materia.filter((a) => a.pct != null).map((a) => (
                    <div key={a.materia} className="flex items-center gap-2 text-xs">
                      <span className="w-28 truncate text-muted-foreground">{a.materia}</span>
                      <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
                        <div className={`h-full rounded-full ${a.pct >= 70 ? "bg-emerald-500" : a.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${a.pct}%` }} />
                      </div>
                      <span className="w-9 text-right tabular-nums">{a.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
              {(rel.onde_focar || []).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  🎯 Onde focar: <strong className="text-foreground">{rel.onde_focar.join(", ")}</strong>
                </p>
              )}
              {rel.resumo && <p className="text-sm text-foreground/90 border-l-2 border-violet-500/50 pl-3">{rel.resumo}</p>}
              {rel.mensagens === 0 && rel.quizzes === 0 && (
                <p className="text-sm text-muted-foreground">Semana quieta por aqui. Bora começar um chat ou um quiz? 🙂</p>
              )}
            </>
          )}
          {!carregando && !rel && <p className="text-sm text-muted-foreground">Não consegui carregar o relatório agora. Tenta de novo daqui a pouco.</p>}
        </div>
      )}
    </motion.div>
  );
}

export default function Hub() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [progresso, setProgresso] = useState(null);
  const [recursos, setRecursos] = useState(null);

  useEffect(() => {
    api.get("/hub/summary", { silent: true }).then((r) => setSummary(r.data)).catch(() => {});
    api.get("/me/progresso", { silent: true }).then((r) => setProgresso(r.data)).catch(() => {});
    api.get("/me/uso", { silent: true }).then((r) => setRecursos(r.data.recursos || {})).catch(() => {});
  }, []);

  const s = summary || {};
  const firstName = (user?.name || "aluno").trim().split(" ")[0];
  const today = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";

  // Ticker só com dados reais do aluno
  const tickerItems = useMemo(() => {
    const arr = [];
    if (s.exam_mode && s.days_to_exam != null) {
      arr.push({ icon: GraduationCap, text: `Prova de ${s.exam_mode.subject} em ${s.days_to_exam} dia${s.days_to_exam === 1 ? "" : "s"} — foca nela!`, to: "/chat" });
    }
    if ((s.due_flashcards ?? 0) > 0) {
      arr.push({ icon: Layers, text: `${s.due_flashcards} flashcard${s.due_flashcards === 1 ? "" : "s"} esperando revisão hoje`, to: "/chat" });
    }
    if ((s.streak ?? 0) > 0) {
      arr.push({ icon: Flame, text: `Ofensiva de ${s.streak} dia${s.streak === 1 ? "" : "s"} — não deixa a chama apagar 🔥` });
    }
    if (s.last_quiz_score != null) {
      arr.push({ icon: GraduationCap, text: `Último quiz: ${Math.round(s.last_quiz_score * 100)}% de acerto — bora subir isso?`, to: "/chat" });
    }
    if (arr.length === 0) {
      arr.push({ icon: Sparkles, text: "Tudo em dia por aqui. Que tal começar um chat ou montar sua rotina?", to: "/chat" });
    }
    return arr;
  }, [s.exam_mode, s.days_to_exam, s.due_flashcards, s.streak, s.last_quiz_score]);

  return (
    <motion.div
      data-testid={HUB.page}
      className="space-y-5"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {/* Header */}
      <motion.div variants={item} className="space-y-1">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{today}</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          {saudacao}, <span className="text-gradient-violet">{firstName}</span>
        </h1>
        <p className="text-muted-foreground text-sm">
          {s.exam_mode ? (
            <>Foco total na prova de <strong className="text-foreground">{s.exam_mode.subject}</strong>.</>
          ) : (
            <>Vamos estudar? Comece um chat ou abra sua rotina.</>
          )}
        </p>
      </motion.div>

      {/* Ticker de prioridades */}
      <Ticker items={tickerItems} />

      {/* Progresso (streak · nível · XP) */}
      <ProgressoCard progresso={progresso} />

      {/* Modo Prova com countdown vivo */}
      {s.exam_mode && <ExamCountdown exam={s.exam_mode} daysToExam={s.days_to_exam} />}

      {/* Bento grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testId={HUB.streak} icon={Flame} label="Ofensiva" tone="orange"
          value={<><CountUp value={s.streak ?? 0} /> dia{(s.streak ?? 0) === 1 ? "" : "s"}</>} />
        <StatCard testId={HUB.flashcardsCard} icon={Brain} label="Flashcards" tone="violet"
          value={<><CountUp value={s.due_flashcards ?? 0} /> pra revisar</>} />
        <StatCard testId={HUB.quizCard} icon={GraduationCap} label="Último quiz" tone="green"
          value={s.last_quiz_score != null ? <CountUp value={Math.round(s.last_quiz_score * 100)} suffix="%" /> : "—"} />
        <StatCard testId={HUB.conversationsCard} icon={MessageSquareText} label="Conversas" tone="blue"
          value={<CountUp value={s.conversations ?? 0} />} />
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-3 gap-3">
        <ActionCard
          to="/chat"
          testId={HUB.goChat}
          icon={MessageSquareText}
          title="Abrir o Chat"
          text="Conversar, gerar quiz, criar flashcards."
        />
        <ActionCard
          to="/chat"
          state={{ openMaterial: true }}
          testId="hub-upload-card"
          icon={UploadCloud}
          title="Enviar material"
          text="PDF, foto do caderno ou print — vira a fonte das respostas."
          dashed
        />
        <ActionCard
          to="/rotina"
          testId={HUB.goRotina}
          icon={Calendar}
          title="Minha rotina"
          text="Sua semana num calendário de verdade."
        />
      </div>

      {/* Relatório semanal (Pro) — cadeado com CTA pros demais */}
      {recursos && <RelatorioSemanal liberado={!!recursos.relatorio_semanal} />}
    </motion.div>
  );
}

function StatCard({ testId, icon: Icon, label, value, tone = "violet" }) {
  const tones = {
    violet: "text-violet-400 bg-violet-500/10",
    orange: "text-orange-400 bg-orange-500/10",
    green: "text-emerald-400 bg-emerald-500/10",
    blue: "text-sky-400 bg-sky-500/10",
  };
  return (
    <motion.div
      variants={item}
      data-testid={testId}
      className="rounded-2xl border border-border bg-card card-depth hover-lift p-4 sm:p-5 flex flex-col gap-3"
    >
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-xl font-bold tracking-tight tabular-nums">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
      </div>
    </motion.div>
  );
}

function ActionCard({ to, state, testId, icon: Icon, title, text, dashed = false }) {
  return (
    <motion.div variants={item}>
      <Link
        to={to}
        state={state}
        data-testid={testId}
        className={`group h-full rounded-2xl border bg-card card-depth hover-lift hover:border-violet-500/40 transition p-5 flex items-start gap-3 ${
          dashed ? "border-dashed border-violet-500/40" : "border-border"
        }`}
      >
        <div className="rounded-xl bg-violet-500/15 p-2.5">
          <Icon className="h-5 w-5 text-violet-400" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{title}</h3>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-violet-400 transition" />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{text}</p>
        </div>
      </Link>
    </motion.div>
  );
}
